import { sanitizeImageUrl } from '@/utils/sanitizers/sanitizeImageUrl'
import { isImagePlaceholder } from '@/utils/editor/imagePlaceholder'
import { regEx } from '@/utils/global/regEx'
// fix when my pr #765 is merged, use shared types
import type { FilledImagesArray } from '@/utils/sanitizers/sanitizeCaseImageUrl'
import { sanitizeCaseImageUrl } from '@/utils/sanitizers/sanitizeCaseImageUrl'
import { logger, EventCategory } from '@/utils/logger'
import { loggerSentry } from '@/utils/loggerSentry'

// Tracks already-reported (caseImage, url) pairs so we don't spam Sentry once
// per render. Per-session — resets on full page reload. Tied to the HOTFIX
// (PR #2571) for the case-image fill-cap bug; long-term fix in issue #2569.
const reportedFillMisses = new Set<string>()

type ImageUrlArgs = {
  url?: string
  editedUrl?: string
  subtype?: string
  caseImage?: string | number
  filledImages?: FilledImagesArray
  logo?: string
  fhLogo?: string
}

/**
 * Determine the actual image URL to use based on image properties and context.
 *
 * This function implements the image URL resolution logic that determines which
 * image source to use based on various conditions like placeholders, logos,
 * edited images, and case images.
 *
 * **Resolution Priority (in order):**
 * 1. Edited image URL (`editedUrl`) - Highest priority for user-edited images
 *    - Used when image has been edited via Pintura or quick actions
 *    - Validated to ensure it's a non-empty string before use
 * 2. Case image (if placeholder and case image available)
 *    - Resolves placeholder URLs to actual case images
 * 3. Logo image (if logoImage subtype and logo available)
 *    - Used for funeral home logo images
 * 4. Extracted URL from CSS url() syntax
 *    - Handles URLs embedded in CSS url() format
 * 5. Sanitized original URL - Fallback to original URL
 *
 * **Important Notes:**
 * - `editedUrl` takes precedence over all other sources to ensure edited images
 *   are always displayed correctly across editor, preview, and PDF environments
 * - If `editedUrl` exists but is invalid (empty, whitespace-only, or malformed),
 *   the function falls through to the next priority level
 * - This priority order ensures that user edits persist and are respected
 *   throughout the application lifecycle
 *
 * @param args - Image properties and context data
 * @param args.url - Original image URL (may be placeholder or CSS url() format)
 * @param args.editedUrl - URL of edited image (from Pintura or quick actions)
 * @param args.subtype - Image subtype (e.g., 'logoImage', 'photo', etc.)
 * @param args.caseImage - Case image index or identifier
 * @param args.filledImages - Array of filled case images
 * @param args.logo - Funeral home logo identifier
 * @param args.fhLogo - Alternative funeral home logo identifier
 * @returns Resolved image URL or null if no valid URL found
 *
 * @example
 * ```typescript
 * // Edited image takes precedence
 * const editedImageUrl = determineImageUrl({
 *   url: 'original.jpg',
 *   editedUrl: 'edited-version.jpg',
 * })
 * // Returns: 'edited-version.jpg'
 *
 * // Placeholder resolves to case image
 * const caseImageUrl = determineImageUrl({
 *   url: 'placeholder.jpg',
 *   subtype: 'photo',
 *   caseImage: 0,
 *   filledImages: ['actual-photo.jpg']
 * })
 * // Returns: 'actual-photo.jpg'
 * ```
 */
export const determineImageUrl = ({
  url,
  editedUrl,
  subtype,
  caseImage,
  filledImages = [],
  logo,
  fhLogo,
}: ImageUrlArgs): string | null => {
  let final

  const caseImageIndex =
    typeof caseImage === 'string' ? parseInt(caseImage, 10) : caseImage || 0
  const caseImageUrl = sanitizeCaseImageUrl(filledImages, caseImageIndex)

  const logoSrc = `${process.env.REACT_APP_S3BUCKET_FH_LOGOS}${logo || fhLogo}`

  const extractedUrl = url?.includes('url(')
    ? url
        ?.match(regEx.enclosedInParentheses)?.[1]
        ?.replace(regEx.singleDoubleQuote, '')
    : null

  // Validate editedUrl before using - must be non-empty string
  // This prevents using stale/invalid edited URLs
  const isValidEditedUrl =
    editedUrl && typeof editedUrl === 'string' && editedUrl.trim().length > 0

  if (isValidEditedUrl) {
    final = editedUrl
  } else if (url && isImagePlaceholder(url) && caseImageUrl) {
    final = caseImageUrl
  } else if (subtype === 'logoImage' && (logo || fhLogo)) {
    final = logoSrc
  } else if (url?.includes('url(') && extractedUrl) {
    final = extractedUrl
  } else {
    final = sanitizeImageUrl(url)
  }

  // HOTFIX (#2569 followup): alarm if a caseImage slot falls through to the
  // raw placeholder URL despite the case actually having images. That means
  // the slot's index exceeded MAX_FILLED_CASE_IMAGES (the regression case
  // we're guarding against) or a case_image record is missing displayUrl.
  // Requiring filledImages.length > 0 skips the loading-window false
  // positive where caseImagesAtom hasn't hydrated yet. Dedupe per-session
  // so we don't spam Sentry on re-renders.
  const fillMissed =
    subtype === 'caseImage' &&
    !!url &&
    isImagePlaceholder(url) &&
    !caseImageUrl &&
    !isValidEditedUrl &&
    (filledImages?.length ?? 0) > 0
  if (fillMissed) {
    const dedupeKey = `${caseImage ?? ''}|${url}`
    if (!reportedFillMisses.has(dedupeKey)) {
      reportedFillMisses.add(dedupeKey)
      const message = 'case-image slot rendered as placeholder (fill missed)'
      const logData = {
        message,
        category: EventCategory.DISCREPANCY,
        data: {
          function: 'determineImageUrl',
          caseImageIndex,
          rawCaseImage: caseImage,
          placeholderUrl: url,
          filledImagesLength: filledImages?.length ?? 0,
          subtype,
        },
      }
      logger.warn(logData)
      loggerSentry.warn(logData)
    }
  }

  // Return null if final URL is invalid (empty, whitespace, or falsy)
  return final && typeof final === 'string' && final.trim().length > 0
    ? final
    : null
}
